import { useState } from "react";
import { motion } from "framer-motion";
import { Users, Calendar, MessageSquare, User, ArrowUpRight } from "lucide-react";
import { WHATSAPP } from "./data";
import SectionHeader from "./SectionHeader";

const ROOM_OPTIONS = [
  "Couples Retreat",
  "Family Room",
  "Group Room (6–10)",
  "Not sure — please advise",
];

export default function BookingForm() {
  const [form, setForm] = useState({
    name: "",
    room: ROOM_OPTIONS[0],
    checkin: "",
    checkout: "",
    guests: 2,
    notes: "",
  });

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const onSubmit = (e) => {
    e.preventDefault();
    const lines = [
      "Hello, I would like to book a room at your homestay.",
      "",
      `• Name: ${form.name || "(not shared)"}`,
      `• Room preference: ${form.room}`,
      `• Check-in: ${form.checkin || "(flexible)"}`,
      `• Check-out: ${form.checkout || "(flexible)"}`,
      `• Guests: ${form.guests}`,
    ];
    if (form.notes) lines.push(`• Notes: ${form.notes}`);
    const msg = encodeURIComponent(lines.join("\n"));
    window.open(`https://wa.me/${WHATSAPP}?text=${msg}`, "_blank", "noopener");
  };

  return (
    <section
      id="book"
      data-testid="booking-form-section"
      className="relative bg-[color:var(--paper-2)] text-[color:var(--ink)]"
    >
      <div className="mx-auto max-w-[1200px] px-5 sm:px-8 lg:px-12 py-16 sm:py-20 lg:py-24">
        <SectionHeader
          align="center"
          eyebrow="Book on WhatsApp"
          title={
            <>
              Send your dates. We'll <span className="font-display-italic text-[color:var(--clay)]">reply</span> personally.
            </>
          }
          intro="Fill this quick form — it opens a ready-to-send WhatsApp message with all your details. No accounts, no waiting."
        />

        <motion.form
          onSubmit={onSubmit}
          data-testid="booking-form"
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.9, ease: [0.2, 0.9, 0.2, 1] }}
          className="mt-10 sm:mt-14 grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-5 bg-[color:var(--paper)] p-6 sm:p-8 rounded-[24px] ring-1 ring-[color:var(--line)]"
        >
          <Field label="Your name" icon={User}>
            <input
              data-testid="booking-name"
              type="text"
              value={form.name}
              onChange={set("name")}
              placeholder="e.g. Arjun Rao"
              className="input-field"
            />
          </Field>

          <Field label="Room preference" icon={Users}>
            <select
              data-testid="booking-room"
              value={form.room}
              onChange={set("room")}
              className="input-field bg-transparent"
            >
              {ROOM_OPTIONS.map((r) => (
                <option key={r}>{r}</option>
              ))}
            </select>
          </Field>

          <Field label="Check-in" icon={Calendar}>
            <input
              data-testid="booking-checkin"
              type="date"
              value={form.checkin}
              onChange={set("checkin")}
              className="input-field"
            />
          </Field>

          <Field label="Check-out" icon={Calendar}>
            <input
              data-testid="booking-checkout"
              type="date"
              value={form.checkout}
              onChange={set("checkout")}
              className="input-field"
            />
          </Field>

          <Field label="Guests" icon={Users}>
            <input
              data-testid="booking-guests"
              type="number"
              min="1"
              max="20"
              value={form.guests}
              onChange={set("guests")}
              className="input-field"
            />
          </Field>

          <Field label="Notes (optional)" icon={MessageSquare}>
            <input
              data-testid="booking-notes"
              type="text"
              value={form.notes}
              onChange={set("notes")}
              placeholder="Meal preferences, arrival time, etc."
              className="input-field"
            />
          </Field>

          <div className="sm:col-span-2 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 pt-2">
            <p className="text-[0.85rem] text-[color:var(--ink)]/60">
              Submitting opens WhatsApp with your details pre-filled.
            </p>
            <button
              data-testid="booking-submit"
              type="submit"
              className="btn-primary self-start sm:self-auto"
              style={{ background: "#25D366", color: "#fff" }}
            >
              Send on WhatsApp <ArrowUpRight size={16} />
            </button>
          </div>
        </motion.form>
      </div>

      <style>{`
        .input-field {
          width: 100%;
          padding: 0.85rem 1rem 0.85rem 2.5rem;
          border-radius: 14px;
          border: 1px solid var(--line);
          background: var(--paper);
          color: var(--ink);
          font-family: 'Manrope', sans-serif;
          font-size: 0.95rem;
          transition: border-color 0.25s ease, box-shadow 0.25s ease;
        }
        .input-field:focus {
          outline: none;
          border-color: var(--forest);
          box-shadow: 0 0 0 3px rgba(22,59,34,0.12);
        }
      `}</style>
    </section>
  );
}

function Field({ label, icon: Icon, children }) {
  return (
    <label className="flex flex-col gap-1.5 relative">
      <span className="font-mono-alt text-[color:var(--forest)]/70">
        {label}
      </span>
      <div className="relative">
        <Icon
          size={16}
          className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[color:var(--moss)] pointer-events-none"
        />
        {children}
      </div>
    </label>
  );
}
